import { Divider, Grid, Typography } from '@mui/material';
import { ActionButton } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { WalletForm } from '@tassat/forms';
import { CustomerModifyFirmAccountReq } from '@tassat/fred-definitions';
import { WalletRecordExtended } from '@tassat/fred-extended-definitions';
import {
    Edit as EditIcon,
    Lock as LockIcon,
    Unlock as UnlockIcon
} from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import { AttributeBadge, AttributeTag, ClosedSymbol } from '@tassat/symbols';
import { WalletTotalBalanceTile } from '@tassat/text';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React, { useMemo } from 'react';

import {
    checkPendingTransactions,
    getTransactions,
    submitWalletForm,
    updateFirmAccount
} from 'Containers/admin/forms/helpers/walletFormFn';
import { getAccountBalancesTileData } from 'Lib/accountBalancesHelper';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { formatEpochDate, obscureAccountNo } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionsStatsByAccountId } from 'Selectors/accountPositions';
import { getAllWalletsByFirmId } from 'Selectors/accounts';
import { getAcgSelectOptions } from 'Selectors/acg';
import { getAccountRelationships, getCurrentRole } from 'Selectors/common';

import type { FirmWalletDetailsHeaderProps } from '.';

export function FirmWalletDetailsHeader(props: FirmWalletDetailsHeaderProps) {
    const {
        classes,
        firm,
        mappedWallet
    } = props;
    const userRole = useAppSelector(getCurrentRole);
    const firmWallets = useAppSelector((state) => getAllWalletsByFirmId(state, mappedWallet.wallet.FirmId));
    const accountRelationships = useAppSelector(getAccountRelationships);
    const accountPositionBalance = useAppSelector((state) => getAccountPositionsStatsByAccountId(state, mappedWallet.wallet.AccountId));
    const acgSelectOptions = useAppSelector((state) => getAcgSelectOptions(state, false, false));
    const { wallet } = mappedWallet;

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] = React.useReducer(
        ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE
    );
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const accountPositionStats = useMemo(() => {
        if (accountPositionBalance){
            if (Object.keys(accountPositionBalance[0].ccy).length === 0 && Object.keys(accountPositionBalance[0].security).length === 0) {
                return;
            }

            return getAccountBalancesTileData(accountPositionBalance);
        }

        return;
    }, [accountPositionBalance]);

    const [ editAccountModalOpen, setEditAccountModalOpen ] = React.useState<boolean>(false);
    const closeEditAccountModal = () => setEditAccountModalOpen(false);

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: wallet,
                setSelectedItem: () => wallet,
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockAccountValidation,
                    [ActionTypes.unlock]: lockAccountValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockAccount,
                    [ActionTypes.unlock]: lockAccount
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    if (!wallet) return <div />;

    const ACGMapping = [ wallet.ACGNumber1, wallet.ACGNumber2, wallet.ACGNumber3 ].filter(acg => acg);

    const lockAccountValidation = (wallet: WalletRecordExtended): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: wallet,
                actionType: wallet.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${wallet.IsLocked ? 'Unlocking' : 'Locking'} Account`,
                confirmBody: `Please confirm ${wallet.IsLocked ? 'unlocking' : 'locking'} user functionality for ${wallet.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockAccount = async (wallet: WalletRecordExtended) => {
        try {
            const accountUpdateValues: Partial<CustomerModifyFirmAccountReq> = { IsLocked: !wallet.IsLocked };

            const response = await Worker.getInstance()
                .updateFirmAccount(wallet.FirmId, wallet.AccountId, accountUpdateValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${wallet.IsLocked ? 'Unlocked' : 'Locked'} Account`,
                        successBody: `Successfully ${wallet.IsLocked ? 'unlocked' : 'locked'} account functionality for ${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${wallet.IsLocked ? 'Unlock' : 'Lock'} Account`,
                        failureBody: `Could not ${wallet.IsLocked ? 'unlock' : 'lock'} account functionality for ${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${wallet.IsLocked ? 'Unlock' : 'Lock'} Account`,
                    failureBody: `Something went wrong ${wallet.IsLocked ? 'unlocking' : 'locking'} account functionality for ${wallet.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.accountHeaderContainer}
                container
                direction='row'
                justifyContent='space-around'
                spacing={0}
                wrap='wrap'
            >
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={4}
                >
                    <Grid
                        className={classes.accountHeaderDetails}
                        container
                        direction='row'
                        spacing={0}
                    >
                        <Typography
                            className={classes.accountNumberDetails}
                            variant='h5'
                        >{obscureAccountNo(wallet?.LinkedAccount?.Account)}
                        </Typography>
                    </Grid>
                    <Typography
                        className={classes.accountName}
                        variant='h4'
                    >{wallet.AccountName}
                    </Typography>
                    <Grid
                        alignItems='center'
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <AttributeBadge
                            active
                            className={classes.statusSymbol}
                            value={wallet.AccessControl ? 'Y' : 'N'}
                        />
                        {wallet.IsPrimary &&
                            <AttributeBadge
                                active
                                className={classes.primarySymbol}
                                value='P'
                            />}
                        {wallet.IsWire &&
                            <AttributeTag
                                attribute='Wire'
                                className={classes.statusSymbol}
                            />}
                        {wallet.IsLiquidityFacility && (
                            <AttributeTag
                                attribute='LF'
                                backgroundColor='#FDF9E2'
                                borderColor='#EDCB26'
                                className={classes.statusSymbol}
                            />
                        )}
                        {wallet.IsLocked &&
                            <LockIcon
                                className={classes.statusSymbol}
                                iconColor={LOCK_ICON_COLOR}
                            />}
                    </Grid>
                    <Typography
                        className={classes.accountNotifyEmail}
                        variant='body2'
                    >{wallet.Email}
                    </Typography>
                    <Divider className={classes.pcgDivider} />
                    <Typography
                        className={classes.detailLabel}
                        variant='body2'
                    >ACG Number(s)
                    </Typography>
                    <Grid
                        container
                        direction='row'
                        spacing={0}
                        wrap='wrap'
                    >
                        {ACGMapping.map(acg => {
                            return (
                                <Typography
                                    className={classes.pcgNumber}
                                    key={acg}
                                    variant='body1'
                                >{acg}
                                </Typography>
                            );
                        })}
                    </Grid>
                </Grid>
                <Divider
                    className={classes.verticalDivider}
                    orientation='vertical'
                />
                <Grid
                    className={classes.accountBalanceTiles}
                    container
                    direction='column'
                    item
                    justifyContent='center'
                    spacing={0}
                    wrap='nowrap'
                    xs={4}
                >
                    {accountPositionStats && <WalletTotalBalanceTile stats={accountPositionStats} />}
                </Grid>
                <Divider
                    className={classes.verticalDivider}
                    orientation='vertical'
                />
                <Grid
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={2}
                >
                    <Typography
                        className={classes.detailLabel}
                        variant='body2'
                    >Notes
                    </Typography>
                    {wallet.Notes ?
                        <Typography
                            className={classes.accountDetail}
                            variant='body1'
                        >{wallet.Notes}
                        </Typography>
                        : (
                            <Typography
                                className={`${classes.accountDetail} ${classes.noNotes}`}
                                variant='body1'
                            >No Notes
                            </Typography>
                        )}
                    <Typography
                        className={classes.accountTimeDetail}
                        variant='body1'
                    >Created {wallet.CreatedAt && wallet.CreatedAt !== '0' ? formatEpochDate(wallet.CreatedAt) : '-'}
                    </Typography>
                    {wallet.IsClosed && wallet.ClosedAt && wallet.ClosedAt !== '0' &&
                        <Typography
                            className={classes.accountTimeDetail}
                            variant='body1'
                        >Closed {formatEpochDate(wallet.ClosedAt)}
                        </Typography>}
                    {!wallet.IsClosed && wallet.ReopenAt && wallet.ReopenAt !== '0' &&
                        <Typography
                            className={classes.accountTimeDetail}
                            variant='body1'
                        >Reopened {formatEpochDate(wallet.ReopenAt)}
                        </Typography>}
                </Grid>
                <Grid
                    alignItems='flex-end'
                    container
                    direction='column'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                    xs={1}
                >
                    <Grid
                        alignItems='center'
                        className={classes.actionButtonsContainer}
                        container
                        direction='row'
                        item
                        justifyContent='flex-end'
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        {wallet.IsClosed &&
                            <ClosedSymbol
                                className={classes.closedTag}
                            />}
                        {userRole.IsManageFirmAllowed &&
                            <ActionButton
                                disabled={wallet.IsClosed || firm?.IsClosed}
                                handleClick={() => lockAccountValidation(wallet)}
                                Icon={wallet.IsLocked ? UnlockIcon : LockIcon}
                                text={wallet.IsLocked ? 'Unlock' : 'Lock'}
                                type='icon'
                            />}
                    </Grid>
                    {userRole.IsManageFirmAllowed &&
                        <ActionButton
                            disabled={firm?.IsClosed}
                            handleClick={() => setEditAccountModalOpen(true)}
                            Icon={EditIcon}
                            text='Edit'
                            type='icon'
                        />}
                </Grid>
            </Grid>
            <FormModal
                Form={WalletForm}
                formProps={{
                    firm: firm,
                    actionType: ActionTypes.edit,
                    firmId: wallet.FirmId,
                    editWallet: mappedWallet,
                    acgSelectOptions,
                    firmWallets: firmWallets,
                    linkedAccountRecord: [wallet?.LinkedAccount],
                    checkPendingTransactions,
                    submitWalletForm,
                    getTransactions,
                    updateFirmAccount,
                    accountRelationships: accountRelationships
                }}
                modalWidth='744px'
                onClose={closeEditAccountModal}
                open={editAccountModalOpen}
                title='Edit Account'
            />
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}